import React, { useEffect } from "react";
import {
  LinearProgress,
  Typography,
  Stack,
  Alert,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import AppModal from "../common/AppModal";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  getJobStatus,
  clearJobState,
} from "../../redux/actions/timetableJobActions";
import { castSolverStatusForDisplay } from "../../helpers/enumHelpers";

interface JobProgressModalProps {
  open: boolean;
  onClose: (success: boolean) => void;
}

const JobProgressModal: React.FC<JobProgressModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { activeJob } = useAppSelector((state) => state.timetableJob);

  const isFailed = activeJob?.scoreExplanation?.startsWith("FAILED:") ?? false;

  const isComplete = activeJob?.solverStatus === "NOT_SOLVING" && !isFailed;

  const isLoading = !isComplete && !isFailed;

  const progressPercent = Math.min(
    100,
    Math.max(0, Math.round((activeJob?.progress ?? 0) * 100)),
  );

  useEffect(() => {
    if (!open || !activeJob?.id || !isLoading) return;

    const interval = setInterval(() => {
      dispatch(getJobStatus(activeJob.id!));
    }, 2000);

    return () => clearInterval(interval);
  }, [open, activeJob?.id, isLoading, dispatch]);

  if (!activeJob) return null;

  const handleClose = (): void => {
    if (isLoading) return;

    dispatch(clearJobState());
    onClose(isComplete);
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title={
        isLoading
          ? "Generating Timetable..."
          : isFailed
            ? "Generation Failed"
            : "Generation Complete"
      }
      width="sm"
      loading={isLoading}
      showCloseIcon={!isLoading}
      actions={
        <Button
          onClick={handleClose}
          disabled={isLoading}
          color={isFailed ? "error" : "secondary"}
          variant="contained"
        >
          {isLoading ? "Running..." : isFailed ? "OK" : "Done"}
        </Button>
      }
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        {isLoading && (
          <>
            <Typography variant="body2" color="text.secondary">
              The AI is optimizing your schedule. Please wait...
            </Typography>

            <LinearProgress
              variant="determinate"
              value={progressPercent}
              color="secondary"
              sx={{ height: 8, borderRadius: 4 }}
            />

            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", display: "block" }}
            >
              Status: {castSolverStatusForDisplay(activeJob.solverStatus)}
            </Typography>

            <Typography variant="body2">
              {progressPercent}% completed
            </Typography>
          </>
        )}

        {isComplete && (
          <Alert
            icon={<CheckCircleIcon fontSize="inherit" />}
            severity="success"
          >
            Timetable generated successfully!
          </Alert>
        )}

        {isFailed && (
          <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
            {activeJob.scoreExplanation?.replace("FAILED: ", "")}
          </Alert>
        )}
      </Stack>
    </AppModal>
  );
};

export default JobProgressModal;
